import React, { useMemo, useState } from "react";
import { Plus, X, Calendar, Clock, User, Filter, ChevronLeft, ChevronRight, Settings, Trash2 } from "lucide-react";
import Wrapper from "../../../components/Wrapper";

export default function TaskHoursPage() {
  const [workers] = useState([
    { id: "w1", name: "Shruti" },
    { id: "w2", name: "Roshni" },
    { id: "w3", name: "Aman" },
  ]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedWorker, setSelectedWorker] = useState("all");
  const [viewMode, setViewMode] = useState("day");
  const [showModal, setShowModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [taskTypes, setTaskTypes] = useState([
    { id: "tt1", name: "Sample Prep", description: "Prepare samples for testing" },
    { id: "tt2", name: "Machine Calibration", description: "Calibrate machine and record output" },
    { id: "tt3", name: "Packaging", description: "Pack finished reagent units" },
  ]);
  const [assignments, setAssignments] = useState([
    { id: "a1", worker_id: "w1", task_type_id: "tt1", date: new Date().toISOString().split("T")[0], hours: 2.5, notes: "Completed 20 samples" },
    { id: "a2", worker_id: "w1", task_type_id: "tt2", date: new Date().toISOString().split("T")[0], hours: 1.5, notes: "" },
    { id: "a3", worker_id: "w2", task_type_id: "tt3", date: new Date().toISOString().split("T")[0], hours: 3, notes: "Batch A" },
  ]);
  const [formData, setFormData] = useState({ worker_id: "", task_type_id: "", hours: "", notes: "" });
  const [newTaskType, setNewTaskType] = useState({ name: "", description: "" });

  const getWeekRangeDates = () => {
    const date = new Date(selectedDate);
    const day = date.getDay();
    const monday = new Date(date);
    monday.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: monday.toISOString().split("T")[0], end: sunday.toISOString().split("T")[0], monday, sunday };
  };

  const filteredByDate = useMemo(() => {
    if (viewMode === "week") {
      const { start, end } = getWeekRangeDates();
      return assignments.filter((a) => a.date >= start && a.date <= end);
    }
    return assignments.filter((a) => a.date === selectedDate);
  }, [assignments, selectedDate, viewMode]);

  const filteredAssignments = useMemo(() => {
    const byWorker = selectedWorker === "all" ? filteredByDate : filteredByDate.filter((a) => a.worker_id === selectedWorker);
    return byWorker.map((a) => ({
      ...a,
      worker: workers.find((w) => w.id === a.worker_id),
      task_type: taskTypes.find((t) => t.id === a.task_type_id),
    }));
  }, [filteredByDate, selectedWorker, workers, taskTypes]);

  const dailyTotals = useMemo(
    () =>
      workers
        .map((worker) => {
          const workerAssignments = filteredAssignments.filter((a) => a.worker_id === worker.id);
          const totalHours = workerAssignments.reduce((sum, a) => sum + Number(a.hours), 0);
          const tasks = workerAssignments.map((a) => ({ task_name: a.task_type?.name || "Unknown", hours: Number(a.hours) }));
          return { worker_id: worker.id, worker_name: worker.name, total_hours: totalHours, tasks };
        })
        .filter((d) => d.total_hours > 0),
    [workers, filteredAssignments]
  );

  const getWeekRangeLabel = () => {
    const { monday, sunday } = getWeekRangeDates();
    return `${monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${sunday.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + (viewMode === "week" ? direction * 7 : direction));
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const exists = assignments.some(
      (a) => a.worker_id === formData.worker_id && a.task_type_id === formData.task_type_id && a.date === selectedDate
    );
    if (exists) return;
    setAssignments((prev) => [
      { id: `a-${Date.now()}`, worker_id: formData.worker_id, task_type_id: formData.task_type_id, date: selectedDate, hours: Number(formData.hours), notes: formData.notes || "" },
      ...prev,
    ]);
    setShowModal(false);
    setFormData({ worker_id: "", task_type_id: "", hours: "", notes: "" });
  };

  const handleDelete = (id) => setAssignments((prev) => prev.filter((a) => a.id !== id));
  const handleAddTaskType = (e) => {
    e.preventDefault();
    if (!newTaskType.name.trim()) return;
    setTaskTypes((prev) => [...prev, { id: `tt-${Date.now()}`, name: newTaskType.name.trim(), description: newTaskType.description.trim() }]);
    setNewTaskType({ name: "", description: "" });
  };
  const handleDeleteTaskType = (id) => {
    setTaskTypes((prev) => prev.filter((t) => t.id !== id));
    setAssignments((prev) => prev.filter((a) => a.task_type_id !== id));
  };

  return (
    <Wrapper>
      <div className="space-y-6 pb-20 lg:pb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-left">
            <h2 className="m-0 text-2xl font-bold text-slate-800 text-left">Task Hours</h2>
            <p className="mt-1 text-slate-500 text-left">Track time spent on different tasks</p>
          </div>
          <div className="flex gap-2 self-start sm:self-auto">
            <button onClick={() => setShowSettingsModal(true)} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 hover:bg-slate-50">
              <Settings size={18} />
              <span className="hidden sm:inline">Manage Tasks</span>
            </button>
            <button onClick={() => setShowModal(true)} className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700">
              <Plus size={18} />
              <span className="hidden sm:inline">Log Hours</span>
            </button>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex items-center gap-2">
              <button onClick={() => navigateDate(-1)} className="rounded-lg p-2 hover:bg-slate-100"><ChevronLeft size={20} /></button>
              <div className="flex min-w-[200px] items-center justify-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                <Calendar size={18} className="text-slate-500" />
                <span className="font-medium">
                  {viewMode === "week"
                    ? getWeekRangeLabel()
                    : new Date(selectedDate).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
              <button onClick={() => navigateDate(1)} className="rounded-lg p-2 hover:bg-slate-100"><ChevronRight size={20} /></button>
            </div>

            <div className="flex items-center gap-2">
              <button onClick={() => setViewMode("day")} className={`rounded-lg px-3 py-2 text-sm font-medium ${viewMode === "day" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Day</button>
              <button onClick={() => setViewMode("week")} className={`rounded-lg px-3 py-2 text-sm font-medium ${viewMode === "week" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Week</button>
            </div>

            <div className="flex items-center gap-2 sm:ml-auto">
              <Filter size={18} className="text-slate-500" />
              <select value={selectedWorker} onChange={(e) => setSelectedWorker(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                <option value="all">All Workers</option>
                {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {dailyTotals.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {dailyTotals.map((dt) => (
              <div key={dt.worker_id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100"><User size={20} className="text-emerald-600" /></div>
                    <span className="font-semibold text-slate-800">{dt.worker_name}</span>
                  </div>
                  <div className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-emerald-700">
                    <Clock size={14} />
                    <span className="font-bold">{dt.total_hours.toFixed(1)}h</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {dt.tasks.map((task, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">{task.task_name}</span>
                      <span className="font-medium text-slate-800">{task.hours.toFixed(1)}h</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 p-4"><h3 className="font-semibold text-slate-800">Task Log</h3></div>
          {filteredAssignments.length === 0 ? (
            <div className="p-8 text-center text-slate-500"><Clock className="mx-auto mb-2 text-slate-300" size={40} /><p>No hours logged for this {viewMode === "week" ? "week" : "date"}</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    {viewMode === "week" && <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>}
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Worker</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Task</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Hours</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Notes</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAssignments.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      {viewMode === "week" && <td className="px-4 py-3 text-sm text-slate-600">{new Date(a.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</td>}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-600">{a.worker?.name?.charAt(0) || "?"}</div>
                          <span className="font-medium text-slate-800">{a.worker?.name || "Unknown"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="rounded bg-blue-50 px-2 py-1 text-sm text-blue-700">{a.task_type?.name || "Unknown"}</span></td>
                      <td className="px-4 py-3 font-semibold text-slate-800">{Number(a.hours).toFixed(1)}h</td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-sm text-slate-500">{a.notes || "-"}</td>
                      <td className="px-4 py-3 text-right"><button onClick={() => handleDelete(a.id)} className="rounded-lg p-2 hover:bg-red-50"><Trash2 size={16} className="text-red-500" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 p-4">
                <h3 className="text-lg font-semibold text-slate-800">Log Task Hours</h3>
                <button onClick={() => setShowModal(false)} className="rounded-lg p-2 hover:bg-slate-100"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4 p-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Worker</label>
                  <select value={formData.worker_id} onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="">Select worker</option>
                    {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Task</label>
                  <select value={formData.task_type_id} onChange={(e) => setFormData({ ...formData, task_type_id: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="">Select task</option>
                    {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Hours</label>
                  <input type="number" step="0.5" min="0.5" max="24" value={formData.hours} onChange={(e) => setFormData({ ...formData, hours: e.target.value })} required className="w-full rounded-lg border border-slate-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Notes (optional)</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full rounded-lg border border-slate-200 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 rounded-lg border border-slate-200 px-4 py-2 hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700">Log Hours</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showSettingsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 p-4">
                <h3 className="text-lg font-semibold text-slate-800">Manage Task Types</h3>
                <button onClick={() => setShowSettingsModal(false)} className="rounded-lg p-2 hover:bg-slate-100"><X size={20} /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <form onSubmit={handleAddTaskType} className="mb-6 rounded-lg bg-slate-50 p-4">
                  <h4 className="mb-3 font-medium text-slate-800">Add New Task Type</h4>
                  <div className="space-y-3">
                    <input type="text" value={newTaskType.name} onChange={(e) => setNewTaskType({ ...newTaskType, name: e.target.value })} placeholder="Task name" className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    <input type="text" value={newTaskType.description} onChange={(e) => setNewTaskType({ ...newTaskType, description: e.target.value })} placeholder="Description (optional)" className="w-full rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                    <button type="submit" className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700">Add Task Type</button>
                  </div>
                </form>

                <h4 className="mb-3 font-medium text-slate-800">Existing Task Types</h4>
                <div className="space-y-2">
                  {taskTypes.map((t) => (
                    <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3">
                      <div>
                        <p className="font-medium text-slate-800">{t.name}</p>
                        {t.description ? <p className="text-sm text-slate-500">{t.description}</p> : null}
                      </div>
                      <button onClick={() => handleDeleteTaskType(t.id)} className="rounded-lg p-2 transition-colors hover:bg-red-50">
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Wrapper>
  );
}

